import { useEffect, useRef } from "react";
import { subMinutes, subHours } from "date-fns";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { useOrgTimeZone } from "@/shared/utils/date/org-time";
import { SHIFT_DEFINITIONS, getShiftRange } from "@/shared/utils/shifts";

const DYNAMIC_PRESETS: Record<string, (now: Date) => Date> = {
  last15min: (now) => subMinutes(now, 15),
  last30min: (now) => subMinutes(now, 30),
  last1hour: (now) => subHours(now, 1),
  last3hours: (now) => subHours(now, 3),
  last12hours: (now) => subHours(now, 12),
  last24hours: (now) => subHours(now, 24),
};

/**
 * Re-resolve a RELATIVE date preset against the current time, once per load.
 *
 * The selected range is persisted as two concrete Dates, so "Last 24 hours" is
 * only the last 24 hours at the moment it was chosen. Come back the next
 * morning — or just reload — and the app is still asking for yesterday's window
 * while claiming "Last 24 hours" in the picker. Shift presets have the same
 * problem: `useDateTimeRangeZoneSync` re-derives them, but only when the
 * location's ZONE changes, and it deliberately skips the first resolution, so a
 * plain reload never refreshes them either.
 *
 * Everything time-scoped inherits that stale window. On `/map` it is especially
 * visible because the alert feed is bounded STRICTLY by it: a stale range shows
 * no markers and an empty alert list, which reads as "alerts are broken" rather
 * than "you are looking at yesterday".
 *
 * Deliberately ONCE, on load — not on an interval. This hook used to hold a
 * 60-second refresh that was disabled behind an unconditional `return`, and the
 * reason is easy to see: moving the range every minute re-keys every
 * time-scoped query in the app, so every page refetches on a timer. Staleness
 * across a reload is the actual complaint; a per-minute treadmill is not the fix
 * for it. Auto-refresh (`useAutoRefreshStore`) is the feature for "keep pulling
 * new data", and it re-fetches without moving the window.
 *
 * A `custom` range is never touched — the operator picked those exact instants.
 */
export const useDateTimeRangeRefresh = () => {
  const selectedPresetKey = useDateTimeRangeStore((s) => s.selectedPresetKey);
  const setValue = useDateTimeRangeStore((s) => s.setValue);
  // Shifts are wall-clock windows AT THE SITE, so they can only be resolved once
  // the location's zone is known. It arrives with the settings fetch, hence the
  // effect keying on it rather than running on bare mount.
  const timeZone = useOrgTimeZone();

  const refreshedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedPresetKey) return;
    // Once per preset per load. Re-running on every zone tick would fight the
    // zone-sync hook, and re-running on every render would fight the picker.
    const stamp = `${selectedPresetKey}@${timeZone}`;
    if (refreshedFor.current === stamp) return;
    refreshedFor.current = stamp;

    const now = new Date();

    const relative = DYNAMIC_PRESETS[selectedPresetKey];
    if (relative) {
      setValue({ fromDate: relative(now), toDate: now });
      return;
    }

    const shift = SHIFT_DEFINITIONS.find((s) => s.key === selectedPresetKey);
    if (shift) {
      const { from, to } = getShiftRange(shift, now, timeZone);
      setValue({ fromDate: from, toDate: to });
    }
  }, [selectedPresetKey, timeZone, setValue]);
};
